import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Workflow,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Smartphone,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { FlowPreview, DEVICE_PRESETS } from '@/components/whatsapp-flows/builder/FlowPreview';
import type { FlowJSON, FlowScreen } from '@/lib/whatsapp-flows';

/**
 * FlowResponsePreview - V1205
 *
 * Renders WhatsApp Flow completion (nfm_reply) messages using a mini FlowPreview.
 * Shows the actual flow screen with customer's selections pre-filled.
 *
 * Features:
 * - Compact card view by default
 * - Expandable to show full phone preview
 * - Pre-fills form values with customer responses
 * - Graceful fallback when flow_json unavailable
 */

interface FlowResponsePreviewProps {
  message: {
    content: string;
    metadata?: {
      flow_response?: Record<string, unknown>;
      nfm_reply?: {
        name?: string;
        body?: string;
        response_json?: string;
      };
      flow_token?: string;
      flow_name?: string;
      screen?: string;
    } | null;
    created_at: string;
  };
  flowJson?: FlowJSON | null;
  flowName?: string;
  isLoading?: boolean;
  error?: string | null;
}

// Fields to hide from compact summary
const HIDDEN_FIELDS = [
  'flow_token',
  '_flow_token',
  '_next_screen',
  '_screen',
  '__internal',
  'version',
];

export function FlowResponsePreview({
  message,
  flowJson,
  flowName,
  isLoading = false,
  error = null,
}: FlowResponsePreviewProps) {
  const { t } = useTranslation('conversations');
  const [isExpanded, setIsExpanded] = useState(false);

  // Parse flow response data
  const flowResponse = useMemo(() => {
    if (message.metadata?.flow_response) {
      return message.metadata.flow_response;
    }
    if (message.content) {
      try {
        return JSON.parse(message.content);
      } catch {
        return {};
      }
    }
    return {};
  }, [message]);

  // Get visible fields for summary
  const visibleFields = useMemo(() => {
    return Object.entries(flowResponse).filter(
      ([key]) => !HIDDEN_FIELDS.some(hidden =>
        key.toLowerCase().includes(hidden.toLowerCase())
      )
    );
  }, [flowResponse]);

  // Find the screen that was completed (if we have flow_json)
  const completedScreen = useMemo(() => {
    if (!flowJson?.screens?.length) return null;

    // Try to find screen by _screen field in response
    const screenId = flowResponse._screen || flowResponse.screen || message.metadata?.screen;
    if (screenId) {
      const screen = flowJson.screens.find(s => s.id === screenId);
      if (screen) return screen;
    }

    // Fallback: find screen that has matching field names
    for (const screen of flowJson.screens) {
      const screenFieldNames = extractFieldNames(screen);
      const responseFieldNames = Object.keys(flowResponse);
      const matchCount = responseFieldNames.filter(f => screenFieldNames.includes(f)).length;
      if (matchCount > 0) {
        return screen;
      }
    }

    // Last fallback: return last screen (usually completion screen)
    return flowJson.screens[flowJson.screens.length - 1];
  }, [flowJson, flowResponse, message.metadata?.screen]);

  // Create a modified flow with pre-filled values for preview
  const prefilledFlow = useMemo(() => {
    if (!flowJson || !completedScreen) return null;

    // Create a copy with __example__ values set to customer's responses
    const modifiedScreens = flowJson.screens.map(screen => {
      if (screen.id !== completedScreen.id) return screen;

      // Pre-fill data sources with customer values
      const modifiedData = { ...screen.data };
      for (const [key, value] of Object.entries(flowResponse)) {
        if (modifiedData[key]) {
          modifiedData[key] = {
            ...modifiedData[key],
            __example__: value
          };
        }
      }

      return { ...screen, data: modifiedData };
    });

    return { ...flowJson, screens: modifiedScreens };
  }, [flowJson, completedScreen, flowResponse]);

  const displayName = flowName || message.metadata?.flow_name || message.metadata?.nfm_reply?.name || 'WhatsApp Flow';
  const screenTitle = completedScreen?.title || message.metadata?.screen || 'Completed';

  // Loading state
  if (isLoading) {
    return (
      <Card className="p-3 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-green-600 animate-spin" />
          <div className="space-y-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </Card>
    );
  }

  // Error state (fallback to simple summary)
  if (error || !flowJson) {
    return (
      <Card className="overflow-hidden border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
        {/* Header */}
        <div className="px-3 py-2 bg-green-100/50 border-b border-green-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-full bg-green-500">
              <CheckCircle2 className="h-3.5 w-3.5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-green-800">{t('flowResponse.completed')}</p>
              <p className="text-[10px] text-green-600">{displayName}</p>
            </div>
          </div>
          <Badge variant="success" className="text-[10px]">
            <Workflow className="h-3 w-3 mr-1" />
            Flow
          </Badge>
        </div>

        {/* Simple field summary */}
        <div className="p-3 space-y-2">
          {visibleFields.length > 0 ? (
            visibleFields.slice(0, 5).map(([key, value]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{formatFieldLabel(key)}:</span>
                <span className="font-medium text-foreground">{formatFieldValue(value)}</span>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">{t('flowResponse.noExtraData')}</p>
          )}
          {visibleFields.length > 5 && (
            <p className="text-xs text-muted-foreground">
              +{visibleFields.length - 5} campos más
            </p>
          )}
        </div>

        {error && (
          <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 flex items-center gap-2 text-xs text-amber-700">
            <AlertCircle className="h-3 w-3" />
            <span>{t('flowResponse.noPreview')}</span>
          </div>
        )}
      </Card>
    );
  }

  // Full preview with FlowPreview component
  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <Card className="overflow-hidden border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
        {/* Header with expand trigger */}
        <CollapsibleTrigger asChild>
          <div className="px-3 py-2 bg-green-100/50 border-b border-green-200 flex items-center justify-between cursor-pointer hover:bg-green-100/70 transition-colors">
            <div className="flex items-center gap-2">
              <div className="p-1 rounded-full bg-green-500">
                <CheckCircle2 className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-green-800">{t('flowResponse.completed')}</p>
                <p className="text-[10px] text-green-600 flex items-center gap-1">
                  <Smartphone className="h-3 w-3" />
                  {screenTitle}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="success" className="text-[10px]">
                <Workflow className="h-3 w-3 mr-1" />
                {displayName}
              </Badge>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-green-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-green-600" />
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        {/* Collapsed: Show field summary */}
        {!isExpanded && (
          <div className="p-3 space-y-1.5">
            {visibleFields.slice(0, 3).map(([key, value]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-muted-foreground text-xs">{formatFieldLabel(key)}:</span>
                <span className="font-medium text-foreground text-xs truncate max-w-[60%]">
                  {formatFieldValue(value)}
                </span>
              </div>
            ))}
            {visibleFields.length > 3 && (
              <p className="text-[10px] text-green-600 text-center pt-1">
                Click para ver vista completa
              </p>
            )}
          </div>
        )}

        {/* Expanded: Show FlowPreview */}
        <CollapsibleContent>
          <div className="p-4 bg-gray-50 border-t border-green-200">
            <div className="flex justify-center" style={{ transform: 'scale(0.5)', transformOrigin: 'top center' }}>
              <FlowPreview
                flow={prefilledFlow!}
                currentScreenId={completedScreen!.id}
                onNavigate={() => {}} // Read-only, no navigation
                deviceType="generic"
                businessName="Business"
                viewOnly={true}
              />
            </div>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Helper: Extract field names from a screen's layout
function extractFieldNames(screen: FlowScreen): string[] {
  const names: string[] = [];

  function traverse(children: Array<Record<string, unknown>>) {
    for (const child of children) {
      if (child.name) {
        names.push(child.name);
      }
      if (child.children) {
        traverse(child.children);
      }
      if (child.then) {
        traverse(child.then);
      }
    }
  }

  if (screen.layout?.children) {
    traverse(screen.layout.children);
  }

  return names;
}

// Helper: Format field label
function formatFieldLabel(key: string): string {
  const labels: Record<string, string> = {
    'selected_product_id': 'Producto',
    'selected_brand': 'Marca',
    'tire_size': 'Medida',
    'quantity': 'Cantidad',
    'customer_name': 'Nombre',
    'appointment_date': 'Fecha',
  };

  if (labels[key]) return labels[key];

  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

// Helper: Format field value
function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    if (value.title || value.name) return value.title || value.name;
    return JSON.stringify(value);
  }
  return String(value);
}

export default FlowResponsePreview;
